import { forwardRef, useMemo } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Fragment } from 'react'
import {
    addDays,
    differenceInCalendarDays,
    endOfMonth,
    endOfDay,
    format,
    isAfter,
    isBefore,
    parseISO,
    startOfDay,
    startOfMonth,
} from 'date-fns'
import useDates from 'services/hooks/useDate'
import TrackerDayDiv from './TrackerDayDiv'
import { useGetProjectTrackerProjects, useMyAccount } from 'services/api/task-management/trackerHooks'

const dayofWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const dayKey = (date) => format(date, 'dd MMMM yyyy')

const clamp = (date, min, max) => {
    if (isBefore(date, min)) return min
    if (isAfter(date, max)) return max
    return date
}

function buildEventsByDayKey(projects = [], monthStart, monthEnd) {
    const map = {}

    const visibleStart = startOfDay(monthStart)
    const visibleEnd = endOfDay(monthEnd)

    for (const p of projects) {
        const sRaw = p?.start_date ? parseISO(p.start_date) : null
        const eRaw = p?.deadline ? parseISO(p.deadline) : null
        if (!sRaw || !eRaw) continue

        const projStart = startOfDay(sRaw)
        const projEnd = endOfDay(eRaw)

        if (isAfter(projStart, visibleEnd) || isBefore(projEnd, visibleStart)) continue

        const clippedStart = clamp(projStart, visibleStart, visibleEnd)
        const clippedEnd = clamp(projEnd, visibleStart, visibleEnd)

        const durationDays = differenceInCalendarDays(startOfDay(eRaw), startOfDay(sRaw)) + 1

        let cursor = clippedStart
        while (!isAfter(cursor, clippedEnd)) {
            const key = dayKey(cursor)
            if (!map[key]) map[key] = []

            map[key].push({
                ...p,
                title: p?.name ?? 'Untitled Project',
                startDate: p?.start_date,
                endDate: p?.deadline,
                durationDays,
            })

            cursor = addDays(cursor, 1)
        }
    }

    return map
}

function filterProjects(projects = [], selectedStaffIds = [], selectedProjectIds = []) {
    const staffIdSet = new Set(selectedStaffIds.filter(Boolean).map((id) => String(id)))
    const projectIdSet = new Set(selectedProjectIds.filter(Boolean).map((id) => String(id)))

    return projects.filter((p) => {
        const matchesProject = !projectIdSet.size || projectIdSet.has(String(p?.id))
        const matchesStaff =
            !staffIdSet.size ||
            (p?.members || []).some((m) => staffIdSet.has(String(m?.staff_id)) || staffIdSet.has(String(m?.staff?.id)))

        return matchesProject && matchesStaff
    })
}

function normalizeProjectStatus(raw) {
    const s = String(raw || '').toLowerCase().trim()
    if (s === 'on_going' || s === 'ongoing' || s === 'on-going') return 'ongoing'
    if (s === 'completed' || s === 'complete') return 'completed'
    return s
}

const TrackerMonthlyCalendar = forwardRef(
    ({ calendarValue, filters, apiFilter, selectedProjectIds = [], onDayClick, onEventClick, selectedEmployees = [] }, ref) => {
        const { currentMonth } = useDates({ date: calendarValue })

        const theme = useTheme()
        const sm = useMediaQuery(theme.breakpoints.up('sm'))
        const mobile_l = useMediaQuery(theme.breakpoints.up('mobile_l'))

        const capitalizeFirstLetter = (string) =>
            string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()

        const monthStart = useMemo(() => startOfMonth(calendarValue), [calendarValue])
        const monthEnd = useMemo(() => endOfMonth(calendarValue), [calendarValue])

        const selectedStaffIds = useMemo(() => {
            return (apiFilter || []).filter(Boolean)
        }, [apiFilter])

        const { data: myAccRes } = useMyAccount()
        const myStaffId = myAccRes?.data?.staff?.id

        const effectiveStaffIds = useMemo(() => {
            if (selectedStaffIds.length > 0) return selectedStaffIds
            if (selectedProjectIds.length > 0) return []
            return myStaffId ? [myStaffId] : []
        }, [selectedStaffIds, selectedProjectIds, myStaffId])

        const projectParams = useMemo(
            () => ({
                start: monthStart.toISOString(),
                end: monthEnd.toISOString(),
            }),
            [monthStart, monthEnd]
        )

        const { data: projectsRes, isLoading } = useGetProjectTrackerProjects(projectParams, {
            enabled: true,
        })

        const backendProjects = useMemo(() => {
            return projectsRes?.data?.projects ?? []
        }, [projectsRes?.data?.projects])

        // const visibleProjects = useMemo(() => {
        //     return filterProjectsBySelectedStaff(backendProjects, selectedStaffIds)
        // }, [backendProjects, selectedStaffIds])

        const visibleProjects = useMemo(() => {
            const activeProjects = backendProjects.filter(
                (p) => normalizeProjectStatus(p?.status) !== 'completed'
            )

            return filterProjects(activeProjects, effectiveStaffIds, selectedProjectIds)
        }, [backendProjects, effectiveStaffIds, selectedProjectIds])

        const eventsByDayKey = useMemo(() => {
            return buildEventsByDayKey(visibleProjects, monthStart, monthEnd)
        }, [visibleProjects, monthStart, monthEnd])

        const getDayEvents = (dayFullDate) => eventsByDayKey[dayFullDate] || []

        return (
            <Box width={1} height={1}>
                {/* Header row */}
                <Box
                    width={1}
                    display="flex"
                    flexWrap="wrap"
                    sx={{
                        overflow: 'hidden',
                        position: 'sticky',
                        top: '0',
                        background: 'white',
                        zIndex: '1',
                        boxShadow: '1px 1px 2px rgb(0 0 0 / 0.10)',
                        borderTop: '1px solid #E5E5E5',
                        borderLeft: '1px solid #E5E5E5',
                        borderRight: '1px solid #E5E5E5',
                    }}
                >
                    {dayofWeek.map((day) => (
                        <Box
                            key={day}
                            sx={{
                                width: 'calc(100% / 7)',
                                height: '50px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Typography variant="body2" color="light-gray-text.main" sx={{ fontWeight: sm ? 'bold' : '' }}>
                                {sm ? day : capitalizeFirstLetter(day)}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box width={1} display="flex" flexWrap="wrap" sx={{ borderRight: '1px solid #E5E5E5' }}>
                    {currentMonth.map((day, index) => {
                        const dayEvents = getDayEvents(day.fullDate)

                        // FIRST day
                        if (index === 0) {
                            const offset = (day.hari - 1 + 7) % 7
                            return (
                                <Fragment key={index}>
                                    {Array.from({ length: offset }, (_, k) => (
                                        <Box
                                            key={k}
                                            sx={{
                                                width: 'calc(100% / 7)',
                                                height: mobile_l ? '180px' : '140px',
                                                borderLeft: '1px solid #E5E5E5',
                                                borderTop: '1px solid #E5E5E5',
                                                background: '#F6F6F6',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    ))}

                                    <TrackerDayDiv
                                        day={day}
                                        ref={ref}
                                        index={index}
                                        value={calendarValue}
                                        filters={filters}
                                        isLoading={isLoading}
                                        events={dayEvents}
                                        onDayClick={onDayClick}
                                        onEventClick={onEventClick}
                                        mobile_l={mobile_l}
                                        showMembers={selectedStaffIds.length > 0}
                                        members={selectedEmployees}
                                    />
                                </Fragment>
                            )
                        }

                        if (index === currentMonth.length - 1) {
                            return (
                                <Fragment key={index}>
                                    <TrackerDayDiv
                                        day={day}
                                        ref={ref}
                                        index={index}
                                        value={calendarValue}
                                        filters={filters}
                                        isLoading={isLoading}
                                        events={dayEvents}
                                        onDayClick={onDayClick}
                                        onEventClick={onEventClick}
                                        mobile_l={mobile_l}
                                        showMembers={selectedStaffIds.length > 0}
                                        members={selectedEmployees}
                                    />

                                    {day.hari !== 0
                                        ? Array.from({ length: 7 - day.hari }, (_, k) => (
                                            <Box
                                                key={k}
                                                sx={{
                                                    width: 'calc(100% / 7)',
                                                    height: mobile_l ? '180px' : '140px',
                                                    borderLeft: '1px solid #E5E5E5',
                                                    borderTop: '1px solid #E5E5E5',
                                                    background: '#F6F6F6',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        ))
                                        : null}
                                </Fragment>
                            )
                        }

                        return (
                            <TrackerDayDiv
                                key={index}
                                day={day}
                                index={index}
                                ref={ref}
                                value={calendarValue}
                                filters={filters}
                                isLoading={isLoading}
                                events={dayEvents}
                                onDayClick={onDayClick}
                                onEventClick={onEventClick}
                                mobile_l={mobile_l}
                                    showMembers={selectedStaffIds.length > 0}
                                    members={selectedEmployees}
                                />
                        )
                    })}
                </Box>
            </Box>
        )
    }
)

export default TrackerMonthlyCalendar
